import { useEffect, useState } from "react";
import { api, ApiError, type ArchivedDocument, type Company } from "../api";
import type { Dictionary } from "../i18n";

/**
 * Podgląd oryginału obok kreatora księgowania.
 *
 * Kreator pyta o numer, daty i kwoty — wszystko to stoi na dokumencie, który właśnie wpłynął.
 * Bez podglądu trzeba go otwierać w drugiej karcie i skakać tam i z powrotem przy każdym polu,
 * a przy przepisywaniu kwot to jest dokładnie ten moment, w którym mylą się cyfry.
 *
 * Plik bierzemy tą samą drogą, co reszta archiwum: najpierw pytamy o **podpisany adres** z magazynu
 * (wtedy przeglądarka pobiera obraz wprost, bez przechodzenia przez nasze API), a gdy magazyn ich
 * nie umie — ściągamy treść żądaniem z tokenem i pokazujemy ją z pamięci. Adres z pamięci
 * zwalniamy przy zamknięciu, żeby otwarcie dziesięciu dokumentów po kolei nie zostawiło
 * dziesięciu kopii pliku w karcie.
 */
export function DocumentPreview({
  company,
  document,
  t,
}: {
  company: Company;
  document: ArchivedDocument;
  t: Dictionary;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    void (async () => {
      setUrl(null);
      setError(null);
      try {
        const link = await api.documentLink(company.id, document.id);
        if (link.url) {
          if (!cancelled) setUrl(link.url);
          return;
        }
        const file = await api.documentContent(company.id, document.id);
        objectUrl = URL.createObjectURL(file.blob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setUrl(objectUrl);
      } catch (failure) {
        if (!cancelled)
          setError(
            failure instanceof ApiError ? failure.message : String(failure),
          );
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [company.id, document.id]);

  const image = (document.contentType ?? "").startsWith("image/");
  const pdf = document.contentType === "application/pdf";

  return (
    <aside className="panel">
      <div className="hd2">
        <b>{t.wizard.preview}</b>
        {url && (
          <a
            className="btn ghost sm"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t.wizard.previewOpen}
          </a>
        )}
      </div>

      {error && <p className="hint">{error}</p>}
      {!url && !error && <p className="muted">{t.loading}</p>}

      {/* Wysokość wiążemy z oknem, a nie z pikselami: podgląd ma się zmieścić obok pól,
          a nie zepchnąć ich pod ekran */}
      {url && image && (
        <img
          src={url}
          alt={document.fileName}
          style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
        />
      )}
      {url && pdf && (
        <object
          data={url}
          type="application/pdf"
          aria-label={document.fileName}
          style={{ width: "100%", height: "70vh" }}
        >
          <p className="hint">{t.wizard.previewUnsupported}</p>
        </object>
      )}
      {url && !image && !pdf && (
        <p className="hint">{t.wizard.previewUnsupported}</p>
      )}
    </aside>
  );
}
